import {
    Box,
    Button,
    ButtonGroup,
    Flex,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Spinner,
    Tag,
    TagLabel,
    Text,
} from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { Asset } from '../../_openapi/portfolio-optimizer';
import { useAssetClearPriceOverride, useAssetCreatePriceOverride, useAssetPrice } from '../../api/pricing';
import { formatDateTime, parseDate } from '../../utils/dateUtils';
import { moneyCol, numberCol, textCol } from '../../widgets/Table/columnHelpers/colDef';
import { Table } from '../../widgets/Table/Table';

interface PriceInfo {
    side: string;
    price?: number;
    size?: number;
}

const columnHelper = createColumnHelper<PriceInfo>();

export const AssetPricing = ({ assetData }: { assetData: Asset }) => {
    const { data, isLoading } = useAssetPrice(assetData.sId);

    const assetPrice = useMemo(() => {
        if (!data) {
            return null;
        }

        return {
            source: data?.source,
            timestamp: data?.timestamp,
            rows: [
                {
                    side: 'Bid',
                    price: data?.bid,
                    size: data?.bidSize,
                },
                {
                    side: 'Ask',
                    price: data?.ask,
                    size: data?.askSize,
                },
            ],
        };
    }, [data]);

    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
            enableSorting: false,
        },
        data: (assetPrice?.rows || []) as PriceInfo[],
        columns: [
            columnHelper.accessor('side', textCol({ id: 'side' })),
            columnHelper.accessor('price', moneyCol({ id: 'price', format: { fractionDigits: 4 } })),
            columnHelper.accessor('size', numberCol({ id: 'size' })),
        ],
        getCoreRowModel: getCoreRowModel(),
    });

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <Box>
            {assetPrice ? (
                <Box>
                    <Flex justify="space-between" mb={2}>
                        <Tag colorScheme={assetPrice?.source === 'MANUAL' ? 'red' : 'gray'}>
                            <TagLabel>{assetPrice?.source}</TagLabel>
                        </Tag>
                        {assetPrice?.timestamp && (
                            <Tag>
                                <TagLabel>{formatDateTime(parseDate(assetPrice?.timestamp))}</TagLabel>
                            </Tag>
                        )}
                    </Flex>
                    <Table table={table} />
                </Box>
            ) : (
                <Text>
                    <i>No pricing data found for security</i>
                </Text>
            )}

            <PricingOverride sId={assetData.sId} isManual={assetPrice?.source === 'MANUAL'} />
        </Box>
    );
};

const PricingOverride = ({ sId, isManual }: { sId: string; isManual: boolean }) => {
    const { mutate: createPriceOverride } = useAssetCreatePriceOverride();
    const { mutate: clearPriceOverride } = useAssetClearPriceOverride();
    const [overrideValue, setOverrideValue] = useState('');

    return (
        <Flex alignItems="center" p={3} background="gray.800" borderRadius="md" gap={4} mt={10}>
            <Text>Override Price</Text>
            <NumberInput size="sm" precision={2} step={0.01} value={overrideValue} onChange={setOverrideValue}>
                <NumberInputField />
                <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                </NumberInputStepper>
            </NumberInput>
            <ButtonGroup ml="auto" size="sm">
                <Button
                    colorScheme="teal"
                    onClick={() => {
                        createPriceOverride({ sId, price: overrideValue });
                        setOverrideValue('');
                    }}
                    isDisabled={!overrideValue.length}
                >
                    Submit
                </Button>
                <Button colorScheme="red" onClick={() => clearPriceOverride(sId)} isDisabled={!isManual}>
                    Remove
                </Button>
            </ButtonGroup>
        </Flex>
    );
};
